import { ExternalLink, Eye, FileDown, FileText, RefreshCw, UserCheck } from 'lucide-react';
import { useMemo, useState } from 'react';

import LockedCcChips from '@/components/common/LockedCcChips';
import { EmailBlockComposer } from '@/components/sales/wizard/EmailBlockComposer';
import { EmailSendPrereqNotice } from '@/components/sales/wizard/EmailSendPrereqNotice';
import { LeadEmailLogList } from '@/components/sales/wizard/LeadEmailLogList';
import { EmailPreviewDialog, Kv, LinkChip } from '@/components/sales/wizard/shared';
import { useToast } from '@/components/ui';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { type LeadDetailsForm } from '@/features/sales-rm/leadForm';
import {
  useEmailComposer,
  useEmailSendPrereqs,
  useFromSelector,
  useLeadEmailSent,
  useLeadPdfDownload,
  useLeadTcLinks,
  useLeadTcStatus,
  useServerDevMode,
} from '@/hooks';
import {
  inr,
  type LeadRecord,
  type LeadStatusValue,
  type PaymentMode,
  type PayStatus,
  type ProductDiscount,
  type ProductInfo,
  type SetupQuoteResult,
  type TcStatus,
  leadFullName,
} from '@/lib/sales-metrics';
import { encodeSnapshot } from '@/lib/snapshot';
import { buildDetailedQuotation, buildWizardQuotationWithDiscount } from '@/lib/wizardQuotation';
import { type BankAccountOption } from '@/services/companySettings.service';
import { type TcItemStatus, type TcType } from '@/services/leads.service';
import { ROUTES, type RenewalFrequencyValue } from '@/utils/constants';

// The two quotation templates this step can send. A successfully-sent log for
// either (persisted server-side) unlocks "Update and Proceed", so the gate
// survives a refresh / reopening the lead (Fix 3).
const CONFIRMATION_SLUGS = ['quotation-draft', 'quotation-draft-discount'];

const TC_OPTIONS: TcStatus[] = ['Not sent', 'Not Confirmed', 'Confirmed'];
// Only the two statuses an RM actually sets. 'Client' is not offered: that
// promotion is owned by the "Onboard Client" button in Step 5, which is what
// runs the T&C / quotation-reference / payment gates - and the server rejects it
// on this PATCH anyway, so offering it here could only ever produce an error.
// An already-onboarded lead still needs 'Client' as a renderable option or the
// select would silently show the wrong value; see LEAD_OPTIONS below.
const EDITABLE_LEAD_OPTIONS: LeadStatusValue[] = ['Lead', 'Future Client'];
const LEAD_OPTIONS = (current: LeadStatusValue): LeadStatusValue[] =>
  current === 'Client' ? [...EDITABLE_LEAD_OPTIONS, 'Client'] : EDITABLE_LEAD_OPTIONS;

const TONE_SELECT_CLASS: Record<'success' | 'warning' | 'neutral', string> = {
  success: 'border-success/40 bg-success/10 text-success font-semibold',
  warning: 'border-warning bg-warning/20 text-foreground font-semibold',
  neutral: 'border-input bg-background text-foreground',
};
// Status box: stacks on phones, becomes a single compact wrap-row from lg up.
const STATUS_CARD =
  'flex flex-col gap-2 rounded-lg border border-border bg-background px-3 py-2.5 lg:flex-row lg:flex-wrap lg:items-end lg:justify-between lg:gap-x-4';
const STATUS_CELL = 'grid min-w-[8.5rem] flex-1 gap-1 sm:flex-none sm:w-40';
const COMPACT_BTN = 'h-11 gap-1 px-3 text-xs sm:h-7 sm:px-2';
const tcTone = (s: TcStatus): keyof typeof TONE_SELECT_CLASS =>
  s === 'Confirmed' ? 'success' : s === 'Not Confirmed' ? 'warning' : 'neutral';
const leadTone = (s: LeadStatusValue): keyof typeof TONE_SELECT_CLASS =>
  s === 'Client' ? 'success' : s === 'Future Client' ? 'warning' : 'neutral';

// Auto T&C acceptance badges (per document type), driven by the acceptance
// ledger - separate from the legacy manual "Status of T&C" select above.
const TC_TYPE_LABEL: Record<TcType, string> = {
  ALL: 'All Products',
  MFTOOLS: 'MFTools',
  API: 'APIs',
};
const TC_ITEM_LABEL: Record<TcItemStatus, string> = {
  accepted: 'Accepted',
  sent: 'Sent',
  pending: 'Not sent',
  not_required: '',
};
const TC_ITEM_TONE: Record<TcItemStatus, string> = {
  accepted: 'bg-success/15 text-success',
  sent: 'bg-warning/20 text-foreground',
  pending: 'bg-secondary text-muted-foreground',
  not_required: '',
};

export interface StatusPatch {
  // T&C status is fully auto-managed server-side (syncLeadTcStatus, derived from
  // the acceptance ledger). The RM can no longer pin it manually, so it is not
  // part of this patch. Fields are optional so this same handler serves Step 3
  // (conversion status) and Step 4 (payment/transaction).
  // 'Client' is excluded by type: only onboardLead may set it, and the server
  // rejects it on the PATCH this patch feeds.
  leadStatus?: Extract<LeadStatusValue, 'Lead' | 'Future Client'>;
  paymentStatus?: PayStatus;
  paymentAmount?: number;
  paymentMode?: PaymentMode | null;
  // Payment transaction number/reference recorded in Step 4 (Fix 1).
  transactionRef?: string | null;
  // How often the client's subscriptions renew after onboarding (Step 4).
  renewalFrequency?: RenewalFrequencyValue;
}

interface StepLeadConfirmationProps {
  lead: LeadRecord;
  form: LeadDetailsForm;
  selectedProducts: string[];
  catalog: ProductInfo[];
  rm: { name: string; email: string; rmId: string };
  isDone: boolean;
  setupQuote: SetupQuoteResult | null;
  onUpdateLead: (patch: StatusPatch) => void;
  onSaveProceed: () => void;
  customPrices: Record<string, number>;
  productTerms: Record<string, number>;
  // Per-product discounts (ADR-019), so the quotation preview can show the same
  // detailed breakdown as the email.
  productDiscounts: Record<string, ProductDiscount>;
  renewalDiscountPercent: number;
  // Shared company bank selection (ADR-020) - applied to both this email and
  // the Step 4 billing email.
  bankOptions: BankAccountOption[];
  bankLabel: string;
  setBankLabel: (label: string) => void;
}

export function StepLeadConfirmation({
  lead,
  form,
  selectedProducts,
  catalog,
  rm,
  isDone,
  setupQuote,
  onUpdateLead,
  onSaveProceed,
  customPrices,
  productTerms,
  productDiscounts,
  renewalDiscountPercent,
  bankOptions,
  bankLabel,
  setBankLabel,
}: StepLeadConfirmationProps) {
  const { toast } = useToast();
  // Dev-only controls below are gated on the API's environment, not the
  // bundle's - see useServerDevMode.
  const isDevServer = useServerDevMode();

  const { items: quotationItems } = useMemo(
    () =>
      buildWizardQuotationWithDiscount({
        catalog,
        selectedProducts,
        customPrices,
        productTerms,
        setupQuote,
        renewalDiscountPercent,
      }),
    [catalog, selectedProducts, customPrices, productTerms, setupQuote, renewalDiscountPercent],
  );

  // The detailed, per-product breakdown shown in the Quotation preview dialog,
  // mirroring the email's quotation table (ADR-020).
  const detailedQuote = useMemo(
    () =>
      buildDetailedQuotation({
        catalog,
        selectedProducts,
        customPrices,
        productTerms,
        productDiscounts,
        setupQuote,
        renewalDiscountPercent,
      }),
    [
      catalog,
      selectedProducts,
      customPrices,
      productTerms,
      productDiscounts,
      setupQuote,
      renewalDiscountPercent,
    ],
  );

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const snapshot = encodeSnapshot({
    leadCode: lead.leadCode,
    firstName: lead.firstName,
    middleName: lead.middleName ?? undefined,
    lastName: lead.lastName ?? undefined,
    arn: lead.arn,
    email: lead.email,
    products: quotationItems.map((p) => ({ name: p.name, price: p.price })),
    languages: [form.lang1, form.lang2, form.lang3].filter(Boolean),
  });
  const q = snapshot ? `?s=${snapshot}` : '';
  const token = encodeURIComponent(lead.leadCode);
  const reviewUrl = `${origin}${ROUTES.REVIEW.replace(':token', token)}${q}`;

  const { fromOptions, fromEmail, setFromChoice } = useFromSelector(rm.email);

  const templateSlug =
    setupQuote && setupQuote.discountAmount > 0 ? 'quotation-draft-discount' : 'quotation-draft';
  const composer = useEmailComposer({
    templateSlug,
    // Stable draft identity: the slug above flips when a discount is toggled in
    // Step 2, but the RM's in-progress edits belong to the one quotation draft.
    draftKey: 'quotation',
    leadId: lead.id,
    enabled: quotationItems.length > 0,
    bankLabel,
    fromEmail,
    defaults: {
      // The recipient is the lead's editable primary contact email (Step 1,
      // saved to lead_details). `lead.email` is a denormalized copy that a
      // manual Step 1 edit does NOT update, so using it left the To stuck on
      // the original address. `form.primaryEmail` always falls back to
      // `lead.email` (see buildInitialForm), so this is safe when empty.
      to: form.primaryEmail || lead.email,
      cc: rm.email,
      bcc: '',
      subject: `AdvisorKhoj: Confirmation & next steps - ${leadFullName(lead)} (${lead.arn})`,
    },
  });

  // Whether a quotation email carrying a real reference number has already gone
  // out for this lead - in this session or a prior one. `requireReferenceNumber`
  // is what makes this a usable gate: the reference (AK/QT/...) is minted only on
  // a successful send, and every later step (payment confirmation, billing
  // handover, the quotation PDF) quotes it, so a send that produced none must not
  // unlock the Payment Plan.
  const {
    alreadySent: confirmationSent,
    referenceNumber: sentReference,
    isLoading: sentLoading,
  } = useLeadEmailSent(lead.id, CONFIRMATION_SLUGS, true, true);

  const [leadStatus, setLeadStatus] = useState<LeadStatusValue>(lead.leadStatus);

  const prereqs = useEmailSendPrereqs();
  const tcStatusQuery = useLeadTcStatus(lead.id);
  const tcBadges = (tcStatusQuery.data?.items ?? []).filter((i) => i.status !== 'not_required');
  // T&C status is fully auto-derived server-side (syncLeadTcStatus) and comes back
  // as overallStatus on the polled query - 'Not sent' before the link goes out,
  // 'Not Confirmed' once it is sent, 'Confirmed' once the lead accepts. It is
  // view-only here; the RM can no longer pin it manually.
  const tc: TcStatus = tcStatusQuery.data?.overallStatus ?? lead.tcStatus;
  // Mentor's rule: the wizard may only advance to Step 4 (and onboard) once the
  // lead has actually accepted the Terms & Conditions.
  const tcConfirmed = tc === 'Confirmed';
  // Both conditions are required before the Payment Plan opens: one quotation
  // email actually sent (so a reference number exists), AND the lead having
  // accepted the Terms & Conditions on the public page. `confirmationSent` is the
  // persisted check rather than `emailSent`, so the gate survives a refresh and an
  // in-session send that never reached the provider can't unlock the step.
  const canProceed = tcConfirmed && confirmationSent;
  const [showTcLinks, setShowTcLinks] = useState(false);
  const tcLinksQuery = useLeadTcLinks(lead.id, showTcLinks);

  const revealTcLinks = () => {
    setShowTcLinks(true);
    void tcStatusQuery.refetch();
    void tcLinksQuery.refetch();
  };

  const [openQuote, setOpenQuote] = useState(false);
  const [openLead, setOpenLead] = useState(false);
  const { download: downloadPdf, downloading } = useLeadPdfDownload(lead.id);

  const sendEmail = () => {
    if (!prereqs.ready) {
      toast.error(prereqs.items[0].message);
      return;
    }
    if (quotationItems.length === 0) {
      toast.error('No products selected in Step 2 - cannot attach quotation');
      return;
    }
    composer.send(
      (message) => toast.error(message),
      () => {
        // Server already moved tc_status -> 'Not Confirmed' (markTcLinksSent);
        // refetch so the read-only dropdown reflects it immediately. The email
        // logs are refreshed centrally by the composer's send handler.
        void tcStatusQuery.refetch();
        toast.success(`Email sent to ${composer.to}`);
      },
    );
  };

  const updateAndProceed = () => {
    // Checked before `confirmationSent`: until the email-log query resolves it is
    // `false`, which is indistinguishable from "never sent". Without this, opening
    // a lead whose quotation went out weeks ago showed "Send the quotation email
    // first" for the first moment - and clicking in that window fired that toast.
    if (sentLoading) {
      toast.error("Still checking this lead's sent emails - try again in a moment.");
      return;
    }
    if (!confirmationSent) {
      toast.error(
        'Send the quotation email first - it generates the reference number this lead is tracked by',
      );
      return;
    }
    if (!tcConfirmed) {
      toast.error('The lead must accept the Terms & Conditions before you can proceed');
      return;
    }
    // Payment status/amount are recorded in Step 4 (Billing Handover) now, so
    // Step 3 only updates the lead conversion status. Omitted entirely for an
    // already-onboarded lead: the server rejects any conversion-status change on
    // a Client, and re-sending the unchanged value would fail the whole update.
    onUpdateLead(leadStatus === 'Client' ? {} : { leadStatus });
    // Re-render the email from the latest saved data so the template content
    // reflects any edits made before this Update.
    void composer.refresh();
    toast.success('Lead updated');
    onSaveProceed();
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-base font-bold leading-tight">Lead Confirmation</h3>
          <p className="text-[11px] text-muted-foreground">
            Send the confirmation email with the T&C link. You can proceed once the lead accepts the
            Terms &amp; Conditions.
          </p>
        </div>
        <Button size="sm" onClick={updateAndProceed} disabled={!canProceed}>
          {isDone ? 'Update' : 'Update and Proceed'}
        </Button>
      </div>

      {/* Status selects (narrow, responsive) and the preview/page-link buttons
          share one compact row so the email composer below gets more vertical
          space. On phones the selects fill the width and the buttons wrap; from
          sm up the selects are fixed-narrow and the buttons sit beside them. */}
      <div className={STATUS_CARD}>
        <div className="flex flex-wrap items-end gap-2">
          <div className={STATUS_CELL}>
            <Label className="text-[11px] text-muted-foreground">Status of T&C (auto)</Label>
            <Select value={tc} disabled className={`h-8 text-sm ${TONE_SELECT_CLASS[tcTone(tc)]}`}>
              {TC_OPTIONS.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </Select>
          </div>
          <div className={STATUS_CELL}>
            <Label className="text-[11px] text-muted-foreground">Lead Conversion</Label>
            <Select
              value={leadStatus}
              onChange={(e) => setLeadStatus(e.target.value as LeadStatusValue)}
              // Once onboarded the status is fixed - the server refuses to
              // change a Client's conversion status, so it must not look editable.
              disabled={leadStatus === 'Client'}
              className={`h-8 text-sm ${TONE_SELECT_CLASS[leadTone(leadStatus)]}`}
            >
              {LEAD_OPTIONS(leadStatus).map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </Select>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <Button
            variant="outline"
            size="sm"
            className={COMPACT_BTN}
            onClick={() => setOpenQuote(true)}
            title="Preview Quotation"
          >
            <Eye className="h-3.5 w-3.5" />
            Quotation
          </Button>
          <Button
            variant="outline"
            size="sm"
            className={COMPACT_BTN}
            onClick={() => setOpenLead(true)}
            title="Preview Lead Details"
          >
            <Eye className="h-3.5 w-3.5" />
            Lead Details
          </Button>
          {/* DEV ONLY, on the server's word (useServerDevMode). The lead review
              page is a standalone, link-only view of the lead used to check what
              the client is shown; on a live deployment it is a second surface
              onto client data with no place in the RM's workflow. "Lead Details"
              beside it is the dialog an RM actually uses and always stays. */}
          {isDevServer && (
            <Button
              variant="outline"
              size="sm"
              className={COMPACT_BTN}
              asChild
              title="Dev only: lead details review page"
            >
              <a href={reviewUrl} target="_blank" rel="noreferrer">
                <UserCheck className="h-3.5 w-3.5" />
                Lead
                <ExternalLink className="h-3 w-3 opacity-60" />
              </a>
            </Button>
          )}
        </div>
      </div>

      {tcBadges.length > 0 && (
        <div className="flex flex-col gap-2 rounded-lg border border-border bg-background px-3 py-2">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[11px] font-medium text-muted-foreground">
              T&amp;C acceptance (auto):
            </span>
            {tcBadges.map((item) => (
              <span
                key={item.type}
                className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${TC_ITEM_TONE[item.status]}`}
                title={
                  item.acceptedAt
                    ? `Accepted on ${new Date(item.acceptedAt).toLocaleString('en-IN')}`
                    : undefined
                }
              >
                {TC_TYPE_LABEL[item.type]}
                {': '}
                {TC_ITEM_LABEL[item.status]}
              </span>
            ))}
            <div className="ml-auto flex items-center gap-1.5">
              {/* Always-available refresh of the auto acceptance status, so the RM
                  can re-check without first opening the T&C page(s) (Fix 4). */}
              <Button
                variant="outline"
                size="sm"
                className={COMPACT_BTN}
                onClick={() => {
                  void tcStatusQuery.refetch();
                }}
                disabled={tcStatusQuery.isFetching}
                title="Refresh T&C acceptance status"
              >
                <RefreshCw
                  className={tcStatusQuery.isFetching ? 'h-3.5 w-3.5 animate-spin' : 'h-3.5 w-3.5'}
                />
                Refresh
              </Button>
              {/* DEV ONLY. The button mints and reveals the lead-facing T&C
                  accept link to the RM, which exists so acceptance can be
                  exercised end-to-end on a test lead. In production that is an
                  RM one click away from accepting a client's Terms &
                  Conditions on their behalf - the exact thing the compliance
                  gate (tc_enforce_sent_before_accept) is there to prevent. The
                  Refresh button beside it stays: re-checking status is the
                  operation an RM actually needs. */}
              {isDevServer && (
                <Button
                  variant="outline"
                  size="sm"
                  className={COMPACT_BTN}
                  onClick={revealTcLinks}
                  disabled={tcLinksQuery.isFetching}
                  title="Dev only: open the lead-facing T&C page(s) to verify viewing and acceptance"
                >
                  <FileText className="h-3.5 w-3.5" />
                  {showTcLinks ? 'Refresh links' : 'Open T&C page'}
                </Button>
              )}
            </div>
          </div>
          {showTcLinks && (
            <div className="flex flex-wrap items-center gap-1.5">
              {tcLinksQuery.isLoading && (
                <span className="text-[11px] text-muted-foreground">Generating link(s)…</span>
              )}
              {!tcLinksQuery.isLoading && (tcLinksQuery.data?.length ?? 0) === 0 && (
                <span className="text-[11px] text-success">
                  All applicable T&amp;C already accepted.
                </span>
              )}
              {tcLinksQuery.data?.map((link) => (
                <a
                  key={link.type}
                  href={link.url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-[11px] font-medium text-brand hover:bg-secondary/40"
                >
                  {link.title}
                  <ExternalLink className="h-3 w-3 opacity-60" />
                </a>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="flex flex-col gap-3 rounded-lg border border-border bg-background p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Email to Lead
          </h4>
          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
            {fromOptions.length > 0 && (
              <div className="flex items-center gap-1.5">
                <Label className="text-xs text-muted-foreground">From</Label>
                <Select
                  value={fromEmail}
                  onChange={(e) => setFromChoice(e.target.value)}
                  className="h-9 w-full min-w-0 sm:w-72"
                  aria-label="Sender (From) address for this email"
                >
                  {fromOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Select>
              </div>
            )}
            {bankOptions.length > 0 && (
              <div className="flex items-center gap-1.5">
                <Label className="text-xs text-muted-foreground">Bank</Label>
                <Select
                  value={bankLabel}
                  onChange={(e) => setBankLabel(e.target.value)}
                  className="h-9 w-full min-w-0 sm:w-44"
                  aria-label="Bank account for this email"
                >
                  {bankOptions.map((b) => (
                    <option key={b.label} value={b.label}>
                      {b.label}
                    </option>
                  ))}
                </Select>
              </div>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                void composer.refresh();
              }}
              disabled={composer.isPreviewFetching}
              title="Refresh email contents from the latest data"
            >
              <RefreshCw
                className={`h-4 w-4 ${composer.isPreviewFetching ? 'animate-spin' : ''}`}
              />{' '}
              Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={() => composer.setOpenPreview(true)}>
              <Eye className="h-4 w-4" /> Preview
            </Button>
          </div>
        </div>
        {composer.isPreviewLoading && (
          <p className="text-[11px] text-muted-foreground">
            Loading the rendered template (bank details, reference number)…
          </p>
        )}
        {composer.isPreviewError && (
          <p className="text-[11px] text-danger">
            Could not load the email template - check that &quot;{templateSlug}&quot; exists and is
            active.
          </p>
        )}
        <div className="grid gap-2 sm:grid-cols-2">
          <div className="grid gap-1.5 sm:col-span-2">
            <Label className="text-xs">To</Label>
            <Input
              value={composer.to}
              onChange={(e) => composer.setTo(e.target.value)}
              placeholder="name@example.com, second@example.com"
            />
          </div>
          {/* `content-start` on both cells, or the shorter one does not line up
              with the taller: these are grid items stretched to the row height,
              and a grid's own auto rows stretch to fill it (align-content
              defaults to stretch). With the "Always copied" chips under CC only,
              that pushed the BCC label and input down by half the chip row. */}
          <div className="grid content-start gap-1.5">
            <Label className="text-xs">CC</Label>
            <Input
              value={composer.cc}
              onChange={(e) => composer.setCc(e.target.value)}
              placeholder="comma-separated for multiple"
            />
            <LockedCcChips category="sales" />
          </div>
          <div className="grid content-start gap-1.5">
            <Label className="text-xs">BCC</Label>
            <Input
              value={composer.bcc}
              onChange={(e) => composer.setBcc(e.target.value)}
              placeholder="optional, comma-separated"
            />
          </div>
          <div className="grid gap-1.5 sm:col-span-2">
            <Label className="text-xs">Subject</Label>
            <Input value={composer.subject} onChange={(e) => composer.setSubject(e.target.value)} />
          </div>
        </div>
        <div className="grid gap-1.5">
          <Label className="text-xs">Body</Label>
          <EmailBlockComposer
            items={composer.items}
            dragIndex={composer.dragIndex}
            onDragStart={composer.handleDragStart}
            onDragEnd={composer.handleDragEnd}
            onDragOverIndex={composer.handleDragOverIndex}
            onUpdateRmBlock={composer.updateRmBlock}
            onRemoveItem={composer.removeItem}
            onAddBlock={composer.addBlock}
          />
        </div>
        <div className="flex flex-wrap gap-1.5">
          <LinkChip>Review the Details</LinkChip>
          <LinkChip>Terms and Conditions</LinkChip>
          <LinkChip>Quotation PDF (attached)</LinkChip>
        </div>
        <EmailSendPrereqNotice items={prereqs.items} />
        <div className="flex items-center justify-between pt-1">
          {!canProceed ? (
            <p className="text-[11px] text-muted-foreground">
              {(() => {
                // A neutral line while the log query is in flight: claiming
                // "not sent yet" before we know is worse than saying nothing.
                if (sentLoading) return "Checking this lead's sent emails...";
                if (confirmationSent) {
                  return `Quotation sent (Ref ${sentReference ?? '-'}). Waiting for the lead to accept the Terms & Conditions to enable "Update and Proceed".`;
                }
                return 'Send the quotation email (it mints the reference number and carries the T&C buttons); the lead must then accept to enable "Update and Proceed".';
              })()}
            </p>
          ) : (
            <span />
          )}
          <Button
            onClick={sendEmail}
            disabled={
              !prereqs.ready ||
              quotationItems.length === 0 ||
              !composer.canSend ||
              composer.isSending ||
              composer.isPreviewFetching
            }
          >
            {composer.sendEmailButtonLabel()}
          </Button>
        </div>
      </div>

      <LeadEmailLogList leadId={lead.id} />

      <EmailPreviewDialog
        open={composer.openPreview}
        onOpenChange={composer.setOpenPreview}
        subject={composer.subject}
        bodyHtml={composer.bodyHtml}
      />

      <Dialog open={openQuote} onOpenChange={setOpenQuote}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>Quotation preview</DialogTitle>
            <DialogDescription>
              {leadFullName(lead)} · {lead.arn}
            </DialogDescription>
          </DialogHeader>
          {detailedQuote.rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">No products selected in Step 2.</p>
          ) : (
            <>
              {detailedQuote.hasAum && form.arnAum && (
                <p className="text-sm">
                  <span className="font-semibold">Current AUM:</span> {form.arnAum} Cr
                </p>
              )}
              <div className="overflow-x-auto">
                {/* min-width so the 5-column quote scrolls horizontally on
                    phones rather than cramping every column. */}
                <Table className="min-w-[560px]">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Product</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead className="text-right">Price</TableHead>
                      <TableHead className="text-right">Discount</TableHead>
                      <TableHead className="text-right">Net Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {detailedQuote.rows.map((r) => (
                      <TableRow key={r.name}>
                        <TableCell className="font-medium">{r.name}</TableCell>
                        <TableCell>{r.duration || '-'}</TableCell>
                        <TableCell className="text-right">{inr(r.price)}</TableCell>
                        <TableCell className="text-right">
                          {r.discountAmount > 0 ? r.discountLabel || inr(r.discountAmount) : '-'}
                        </TableCell>
                        <TableCell className="text-right">{inr(r.net)}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell className="font-semibold" colSpan={2}>
                        Sub-Total
                      </TableCell>
                      <TableCell className="text-right">
                        {inr(detailedQuote.totals.grossSubtotal)}
                      </TableCell>
                      <TableCell className="text-right">
                        {detailedQuote.totals.totalDiscount > 0
                          ? inr(detailedQuote.totals.totalDiscount)
                          : '-'}
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        {inr(detailedQuote.totals.netSubtotal)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        colSpan={4}
                      >{`GST @ ${detailedQuote.totals.gstPercentage}%`}</TableCell>
                      <TableCell className="text-right">
                        {inr(detailedQuote.totals.gstAmount)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-bold" colSpan={4}>
                        Total Payable
                      </TableCell>
                      <TableCell className="text-right font-bold">
                        {inr(detailedQuote.totals.totalPayable)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
              {(setupQuote?.additionalArns.length ?? 0) > 0 && (
                <div className="mt-4 overflow-x-auto">
                  <p className="mb-2 text-sm font-semibold">Additional ARNs</p>
                  <Table className="min-w-[420px]">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10">#</TableHead>
                        <TableHead>ARN</TableHead>
                        <TableHead>Client Name</TableHead>
                        <TableHead className="text-right">AUM</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {setupQuote?.additionalArns.map((a, index) => (
                        <TableRow key={a.arn}>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell className="font-medium">{a.arn}</TableCell>
                          <TableCell>{a.clientName}</TableCell>
                          <TableCell className="text-right">{`${a.aum ?? 0} Cr`}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                void downloadPdf('quotation');
              }}
              disabled={quotationItems.length === 0 || downloading !== null}
            >
              {downloading === 'quotation' ? (
                <RefreshCw className="mr-1.5 h-4 w-4 animate-spin" />
              ) : (
                <FileDown className="mr-1.5 h-4 w-4" />
              )}{' '}
              Download PDF
            </Button>
            <Button onClick={() => setOpenQuote(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={openLead} onOpenChange={setOpenLead}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>Lead details preview</DialogTitle>
            <DialogDescription>Read-only snapshot from Step 1.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-4 text-sm">
            <section>
              <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Identity
              </h4>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Kv label="Lead ID" v={String(lead.leadCode)} />
                <Kv label="ARN" v={lead.arn} />
                <Kv label="ARN Holder Name" v={form.arnHolderName} />
                <Kv label="ARN AUM (Cr)" v={form.arnAum} />
                <Kv label="ARN Holder Address" v={form.arnAddress} />
                <Kv label="Legal Name" v={form.legalName} />
                <Kv label="Display Name" v={form.displayName} />
                <Kv label="GSTIN" v={form.gstin} />
                <Kv label="Website" v={form.websiteUrl} />
              </div>
            </section>
            <section>
              <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Contact
              </h4>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Kv label="Primary Number" v={form.primaryNumber} />
                <Kv label="Secondary Number" v={form.secondaryNumber} />
                <Kv label="Primary Email" v={form.primaryEmail} />
                <Kv label="Secondary Email" v={form.secondaryEmail} />
              </div>
            </section>
            <section>
              <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Business Address
              </h4>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Kv label="Address" v={form.address} />
                <Kv label="Pincode" v={form.pincode} />
                <Kv label="City" v={form.city} />
                <Kv label="State" v={form.state} />
              </div>
            </section>
            <section>
              <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Preferences
              </h4>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Kv label="Preferred Language 1" v={form.lang1} />
                <Kv label="Preferred Language 2" v={form.lang2} />
                <Kv label="Preferred Language 3" v={form.lang3} />
                <Kv label="Remarks" v={form.remarks} />
              </div>
            </section>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
